import api from "./api.js";
import StripeMetadataService from "../utils/stripeMetadata.js";
import { httpErrorMessage } from "../utils/httpErrorMessage.js";

class PaymentService {
  static async getStripeConfig() {
    const response = await api.get("/payments/config");
    return response.data;
  }

  static async getPaymentMethods() {
    const response = await api.get("/payments/payment-methods");
    return response.data;
  }

  static async addPaymentMethod(paymentMethodId, billingInfo) {
    const response = await api.post("/payments/add-payment-method", {
      paymentMethodId,
      billingInfo,
    });
    return response.data;
  }

  static async setDefaultPaymentMethod(paymentMethodId) {
    const response = await api.put("/payments/set-default-payment-method", {
      paymentMethodId,
    });
    return response.data;
  }

  static async removePaymentMethod(paymentMethodId) {
    const response = await api.delete(
      `/payments/remove-payment-method/${paymentMethodId}`
    );
    return response.data;
  }

  static async createDomainPaymentIntent(domainData) {
    const currentExpiration = new Date(
      domainData.currentExpirationDate || new Date()
    );
    const effectiveDate = new Date();
    const renewalDate = new Date(currentExpiration);
    renewalDate.setUTCFullYear(
      currentExpiration.getUTCFullYear() + domainData.years
    );

    const metadata = StripeMetadataService.createDomainPaymentMetadata({
      ...domainData,
      userId: domainData.userId || "unknown-user",
      effectiveDate: effectiveDate.toISOString(),
      renewalDate: renewalDate.toISOString(),
    });

    const requestData = {
      amount: domainData.pricePerYear * domainData.years,
      currency: domainData.currency || "eur",
      description: `Domain extension for ${domainData.domainName} - ${
        domainData.years
      } year${domainData.years > 1 ? "s" : ""}`,
      projectId: domainData.newPlanId || domainData.planId,
      effectiveDate: effectiveDate.toISOString(),
      renewalDate: renewalDate.toISOString(),
      metadata,
      clientID: domainData.clientID,
      fullName: domainData.fullName || "Customer",
      emailAddress: domainData.emailAddress || "customer@example.com",
      billingAddress: domainData.billingAddress || "Default Address",
      city: domainData.city || "Default City",
      stateRegion: domainData.stateRegion || "",
      country: domainData.country || "US",
      phone: domainData.phone || "0000000000",
      product: domainData.domainName,
      service: "Domain Extension",
      customerType: domainData.customerType || "person",
      contactName:
        domainData.contactName || domainData.fullName || "Customer",
      companyName: domainData.companyName || "",
      vatNumber: domainData.vatNumber || "",
      registrationNumber: domainData.registrationNumber || "",
    };

    const response = await api.post("/payments/create-intent", requestData);
    return response.data;
  }

  static async createSubscriptionPaymentIntent(subscriptionData) {
    const metadata = StripeMetadataService.createSubscriptionPaymentMetadata({
      ...subscriptionData,
      userId: subscriptionData.userId,
    });

    const requestData = {
      amount: subscriptionData.amount,
      currency: subscriptionData.currency || "eur",
      description: `${
        subscriptionData.planName || "Subscription"
      } Plan - Monthly Subscription`,
      projectId: subscriptionData.projectId,
      metadata,
      clientID: subscriptionData.clientID,
      fullName: subscriptionData.fullName,
      emailAddress: subscriptionData.emailAddress,
      billingAddress: subscriptionData.billingAddress,
      city: subscriptionData.city,
      stateRegion: subscriptionData.stateRegion,
      country: subscriptionData.country,
      phone: subscriptionData.phone,
      product: subscriptionData.product || subscriptionData.planName,
      service: `${subscriptionData.planName || "Subscription"} Service`,
      customerType: subscriptionData.customerType || "person",
      contactName: subscriptionData.contactName,
      companyName: subscriptionData.companyName,
      vatNumber: subscriptionData.vatNumber,
      registrationNumber: subscriptionData.registrationNumber,
    };

    const response = await api.post("/payments/create-intent", requestData);
    return response.data;
  }

  static async getPaymentHistory() {
    try {
      const response = await api.get("/payments/history");
      return response.data;
    } catch (error) {
      const msg = httpErrorMessage(error, "Failed to get payment history");
      return { success: false, error: msg, message: msg };
    }
  }

  static async verifyPaymentStatus(paymentIntentId) {
    try {
      const response = await api.get(`/payments/verify/${paymentIntentId}`);
      return response.data;
    } catch (error) {
      const msg = httpErrorMessage(error, "Failed to verify payment status");
      return { success: false, error: msg, message: msg };
    }
  }
}

export default PaymentService;
